/**
 * @file Require Album Membership higher order component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { MeForAlbumPurchasePhotos } from '../../graphql/queries/account';
import { getAlbumMembership } from '../../util/account';

/**
 * Higher order component for the require album membership functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithAlbumMembershipHOC = WrappedComponent =>
  class extends Component {
    static propTypes = {
      album: PropTypes.shape({
        id: PropTypes.string,
      }),
      albumMembership: PropTypes.object,
      loading: PropTypes.bool,
    };

    render() {
      const { album, albumMembership, loading } = this.props;

      return !loading && !albumMembership ? (
        <Redirect to={`/purchase/album/${album.id}`} />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the require album membership functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RequireAlbumMembership = WrappedComponent => {
  const WithAlbumMembership = WithAlbumMembershipHOC(WrappedComponent);

  const withUserData = graphql(MeForAlbumPurchasePhotos, {
    options: ({ match }) => ({
      variables: {
        albumId: match.params.albumId,
      },
    }),
    props: ({ data: { me, loading }, ownProps: { match } }) => ({
      user: me,
      albumMembership: getAlbumMembership(me, match.params.albumId),
      loading,
    }),
  });

  return compose(withUserData)(WithAlbumMembership);
};

export { WithAlbumMembershipHOC };
export default RequireAlbumMembership;
